import { z } from 'zod';

/**
 * Assinatura, trial e faturamento (M16, seção 11).
 *
 * O plano não é um enfeite de página de preço: ele decide o que a igreja pode
 * fazer hoje. Por isso os limites moram aqui, no contrato — o front mostra a
 * barra de uso com os mesmos números que o servidor usa para recusar.
 */

// ── Planos ───────────────────────────────────────────────────────────────────

export const PLANOS = ['TRIAL', 'SEMENTE', 'COMUNIDADE', 'MINISTERIO', 'REDE'] as const;
export const planoSchema = z.enum(PLANOS);
export type Plano = z.infer<typeof planoSchema>;

export const PLANO_LABEL: Record<Plano, string> = {
  TRIAL: 'Avaliação',
  SEMENTE: 'Semente',
  COMUNIDADE: 'Comunidade',
  MINISTERIO: 'Ministério',
  REDE: 'Rede',
};

/** `null` é ilimitado — e ilimitado aparece na tela como "sem limite". */
export interface LimitesDoPlano {
  pessoasAtivas: number | null;
  congregacoes: number | null;
  /** Crianças com check-in no mês. Criança cadastrada não é cobrada. */
  criancasPorMes: number | null;
  armazenamentoGb: number;
  /** Recursos que o plano abre. */
  whatsapp: boolean;
  kids: boolean;
  roteiro: boolean;
  relatorios: boolean;
  motorDeEscala: boolean;
  aprovacoes: boolean;
  exportacoes: boolean;
  marcaPropria: boolean;
}

export interface PlanoDoProduto {
  plano: Plano;
  nome: string;
  descricao: string;
  /** Em centavos, para não flutuar. Zero é grátis. */
  precoMensal: number;
  limites: LimitesDoPlano;
}

/**
 * A tabela da seção 11.1. Os valores são configuração, não regra de código: a
 * El Shaday muda preço sem tocar em lógica.
 */
export const PLANOS_DO_PRODUTO: readonly PlanoDoProduto[] = [
  {
    plano: 'TRIAL',
    nome: 'Avaliação',
    descricao: '14 dias com tudo liberado, sem cartão. É para a igreja ver funcionando.',
    precoMensal: 0,
    limites: {
      pessoasAtivas: null,
      congregacoes: 1,
      criancasPorMes: null,
      armazenamentoGb: 2,
      whatsapp: true,
      kids: true,
      roteiro: true,
      relatorios: true,
      motorDeEscala: true,
      aprovacoes: true,
      exportacoes: true,
      marcaPropria: false,
    },
  },
  {
    plano: 'SEMENTE',
    nome: 'Semente',
    descricao: 'Para a igreja pequena que quer parar com a escala no papel.',
    precoMensal: 0,
    limites: {
      pessoasAtivas: 25,
      congregacoes: 1,
      criancasPorMes: 0,
      armazenamentoGb: 1,
      whatsapp: false,
      kids: false,
      roteiro: false,
      relatorios: false,
      motorDeEscala: false,
      aprovacoes: false,
      exportacoes: false,
      marcaPropria: false,
    },
  },
  {
    plano: 'COMUNIDADE',
    nome: 'Comunidade',
    descricao: 'WhatsApp, roteiro, repertório, relatórios e o Ministério Infantil.',
    precoMensal: 7_900,
    limites: {
      pessoasAtivas: 100,
      congregacoes: 1,
      criancasPorMes: 150,
      armazenamentoGb: 10,
      whatsapp: true,
      kids: true,
      roteiro: true,
      relatorios: true,
      motorDeEscala: false,
      aprovacoes: false,
      exportacoes: false,
      marcaPropria: false,
    },
  },
  {
    plano: 'MINISTERIO',
    nome: 'Ministério',
    descricao: 'Auto-escala, aprovações e exportações, com até três congregações.',
    precoMensal: 16_900,
    limites: {
      pessoasAtivas: 300,
      congregacoes: 3,
      criancasPorMes: 500,
      armazenamentoGb: 50,
      whatsapp: true,
      kids: true,
      roteiro: true,
      relatorios: true,
      motorDeEscala: true,
      aprovacoes: true,
      exportacoes: true,
      marcaPropria: false,
    },
  },
  {
    plano: 'REDE',
    nome: 'Rede',
    descricao: 'Multi-congregação sem teto, com a marca da igreja nos compartilháveis.',
    precoMensal: 0,
    limites: {
      pessoasAtivas: null,
      congregacoes: null,
      criancasPorMes: null,
      armazenamentoGb: 200,
      whatsapp: true,
      kids: true,
      roteiro: true,
      relatorios: true,
      motorDeEscala: true,
      aprovacoes: true,
      exportacoes: true,
      marcaPropria: true,
    },
  },
];

export const PLANOS_POR_CHAVE: Record<Plano, PlanoDoProduto> = Object.fromEntries(
  PLANOS_DO_PRODUTO.map((plano) => [plano.plano, plano]),
) as Record<Plano, PlanoDoProduto>;

/** A frase que explica "pessoa ativa" — sem letra miúda (seção 11.1). */
/** A janela que define "pessoa ativa". Vale na tela e na recusa do servidor. */
export const DIAS_PARA_SER_ATIVA = 90;

export const DEFINICAO_DE_PESSOA_ATIVA =
  'Pessoa ativa é quem não foi desligada e apareceu em alguma escala nos últimos 90 dias. ' +
  'Criança cadastrada no Ministério Infantil não conta como pessoa ativa e não é cobrada.';

// ── Recursos que o plano abre ────────────────────────────────────────────────

export const RECURSOS = [
  'whatsapp',
  'kids',
  'roteiro',
  'relatorios',
  'motorDeEscala',
  'aprovacoes',
  'exportacoes',
  'marcaPropria',
] as const;
export const recursoSchema = z.enum(RECURSOS);
export type RecursoDoPlano = z.infer<typeof recursoSchema>;

export const RECURSO_LABEL: Record<RecursoDoPlano, string> = {
  whatsapp: 'WhatsApp da igreja',
  kids: 'Ministério Infantil',
  roteiro: 'Roteiro do culto e repertório',
  relatorios: 'Relatórios e indicadores',
  motorDeEscala: 'Auto-escala',
  aprovacoes: 'Aprovação da escala',
  exportacoes: 'Exportar em PDF e planilha',
  marcaPropria: 'Marca da igreja nos compartilháveis',
};

// ── Assinatura ───────────────────────────────────────────────────────────────

export const SUBSCRIPTION_STATUS = [
  'TRIALING',
  'ACTIVE',
  'PAST_DUE',
  'READ_ONLY',
  'FROZEN',
  'CANCELLED',
] as const;
export const subscriptionStatusSchema = z.enum(SUBSCRIPTION_STATUS);
export type SubscriptionStatus = z.infer<typeof subscriptionStatusSchema>;

export const SUBSCRIPTION_STATUS_LABEL: Record<SubscriptionStatus, string> = {
  TRIALING: 'Em avaliação',
  ACTIVE: 'Ativa',
  PAST_DUE: 'Pagamento atrasado',
  READ_ONLY: 'Somente leitura',
  FROZEN: 'Congelada',
  CANCELLED: 'Cancelada',
};

/**
 * O relógio do ciclo de vida (seção 11.2). Nada aqui apaga dado de ninguém: o
 * produto vai fechando a escrita, e a exportação continua aberta o tempo todo.
 */
export const DIAS_DE_TRIAL = 14;
/** Avisos discretos, e sempre com tempo de reagir. */
export const AVISOS_DE_TRIAL = [7, 3, 1] as const;
/** Depois do trial: quinze dias vendo tudo, sem poder criar nem publicar. */
export const DIAS_EM_SOMENTE_LEITURA = 15;
/** Pagamento atrasado ganha uma semana de graça antes de fechar a escrita. */
export const DIAS_DE_GRACA = 7;

/** Quando a assinatura está nesse estado, o produto não aceita escrita. */
export const ESTADOS_SEM_ESCRITA: readonly SubscriptionStatus[] = [
  'READ_ONLY',
  'FROZEN',
  'CANCELLED',
];

export function podeEscrever(status: SubscriptionStatus): boolean {
  return !ESTADOS_SEM_ESCRITA.includes(status);
}

export const CICLOS = ['MONTHLY', 'YEARLY'] as const;
export const cicloSchema = z.enum(CICLOS);
export type CicloDeCobranca = z.infer<typeof cicloSchema>;

export const CICLO_LABEL: Record<CicloDeCobranca, string> = {
  MONTHLY: 'Mensal',
  YEARLY: 'Anual (dois meses de desconto)',
};

/** Quantos meses o ano custa: doze meses pagando dez. */
export const MESES_NO_ANUAL = 10;

export const usoSchema = z.object({
  pessoasAtivas: z.number().int(),
  congregacoes: z.number().int(),
  criancasNoMes: z.number().int(),
  armazenamentoGb: z.number(),
});
export type UsoDaIgreja = z.infer<typeof usoSchema>;

export const limiteSchema = z.object({
  chave: z.string(),
  rotulo: z.string(),
  usado: z.number(),
  limite: z.number().nullable(),
  /** Quanto do limite já foi usado, de 0 a 100. Nulo quando não há limite. */
  porcentagem: z.number().int().nullable(),
  estourado: z.boolean(),
});
export type LimiteNaTela = z.infer<typeof limiteSchema>;

/**
 * O aviso que aparece na faixa do topo. `null` quando não há nada a dizer —
 * uma igreja em dia não precisa ver banner nenhum.
 */
export const avisoDaAssinaturaSchema = z.object({
  tom: z.enum(['informativo', 'atencao', 'bloqueio']),
  titulo: z.string(),
  detalhe: z.string(),
  /** O que a liderança pode fazer a respeito, se puder. */
  acao: z.object({ rotulo: z.string(), destino: z.string() }).nullable(),
});
export type AvisoDaAssinatura = z.infer<typeof avisoDaAssinaturaSchema>;

export const assinaturaSchema = z.object({
  plano: planoSchema,
  planoNome: z.string(),
  status: subscriptionStatusSchema,
  ciclo: cicloSchema,
  precoMensal: z.number().int(),
  /** Dias que faltam no trial. Nulo fora do trial. */
  diasDeTrial: z.number().int().nullable(),
  trialTerminaEm: z.string().datetime().nullable(),
  proximaCobranca: z.string().datetime().nullable(),
  /** Quando o modo somente leitura começou, se começou. */
  somenteLeituraDesde: z.string().datetime().nullable(),
  cancelaNoFimDoCiclo: z.boolean(),
  uso: usoSchema,
  limites: z.array(limiteSchema),
  recursos: z.record(z.string(), z.boolean()),
  /** O que a igreja precisaria reduzir para caber em um plano menor. */
  bloqueiosParaDowngrade: z.array(z.string()),
  podeEscrever: z.boolean(),
  aviso: avisoDaAssinaturaSchema.nullable(),
});
export type Assinatura = z.infer<typeof assinaturaSchema>;

export const trocarDePlanoSchema = z.object({
  plano: planoSchema,
  ciclo: cicloSchema.default('MONTHLY'),
});
export type TrocarDePlanoInput = z.infer<typeof trocarDePlanoSchema>;

export const cancelarSchema = z.object({
  motivo: z.string().trim().min(5, 'Conte o motivo — é assim que o produto melhora').max(500),
  /** Cancelar agora encerra no fim do ciclo já pago. */
  confirmado: z.literal(true, {
    errorMap: () => ({ message: 'Confirme que entende como fica o acesso' }),
  }),
});
export type CancelarInput = z.infer<typeof cancelarSchema>;

// ── Faturas ──────────────────────────────────────────────────────────────────

export const INVOICE_STATUS = ['PENDING', 'PAID', 'OVERDUE', 'REFUNDED', 'CANCELLED'] as const;
export const invoiceStatusSchema = z.enum(INVOICE_STATUS);
export type InvoiceStatus = z.infer<typeof invoiceStatusSchema>;

export const INVOICE_STATUS_LABEL: Record<InvoiceStatus, string> = {
  PENDING: 'Aguardando pagamento',
  PAID: 'Paga',
  OVERDUE: 'Vencida',
  REFUNDED: 'Devolvida',
  CANCELLED: 'Cancelada',
};

export const BILLING_METHODS = ['PIX', 'BOLETO', 'CREDIT_CARD'] as const;
export const billingMethodSchema = z.enum(BILLING_METHODS);
export type BillingMethod = z.infer<typeof billingMethodSchema>;

export const BILLING_METHOD_LABEL: Record<BillingMethod, string> = {
  PIX: 'Pix',
  BOLETO: 'Boleto',
  CREDIT_CARD: 'Cartão de crédito',
};

export const faturaSchema = z.object({
  id: z.string().uuid(),
  status: invoiceStatusSchema,
  valor: z.number().int(),
  metodo: billingMethodSchema.nullable(),
  vencimento: z.string(),
  pagaEm: z.string().datetime().nullable(),
  /** Link do Asaas para pagar ou para a segunda via. */
  linkDePagamento: z.string().nullable(),
  descricao: z.string(),
});
export type Fatura = z.infer<typeof faturaSchema>;

// ── Webhook do Asaas ─────────────────────────────────────────────────────────

export const EVENTOS_DO_ASAAS = [
  'PAYMENT_CREATED',
  'PAYMENT_CONFIRMED',
  'PAYMENT_RECEIVED',
  'PAYMENT_OVERDUE',
  'PAYMENT_REFUNDED',
  'PAYMENT_DELETED',
  'SUBSCRIPTION_CREATED',
  'SUBSCRIPTION_UPDATED',
  'SUBSCRIPTION_DELETED',
] as const;
export const eventoDoAsaasSchema = z.enum(EVENTOS_DO_ASAAS);
export type EventoDoAsaas = z.infer<typeof eventoDoAsaasSchema>;

/** O corpo que o Asaas manda. Só o que o produto usa entra no schema. */
export const asaasWebhookSchema = z.object({
  id: z.string().min(1),
  event: z.string().min(1),
  payment: z
    .object({
      id: z.string(),
      subscription: z.string().nullish(),
      customer: z.string().nullish(),
      value: z.number(),
      billingType: z.string().nullish(),
      dueDate: z.string().nullish(),
      paymentDate: z.string().nullish(),
      invoiceUrl: z.string().nullish(),
      description: z.string().nullish(),
    })
    .nullish(),
  subscription: z
    .object({ id: z.string(), customer: z.string().nullish(), status: z.string().nullish() })
    .nullish(),
});
export type AsaasWebhookInput = z.infer<typeof asaasWebhookSchema>;

/**
 * Fazer o tempo passar, sem esperar quatorze dias.
 *
 * O relógio que roda aqui é o mesmo de produção — só o instante é outro. É o
 * que permite provar o fim do trial numa suíte que roda em segundos.
 */
export const simularRelogioSchema = z.object({
  avancarDias: z.coerce.number().int().min(1).max(400),
});
export type SimularRelogioInput = z.infer<typeof simularRelogioSchema>;

/** O simulador do gateway, para desenvolver e testar sem tocar a rede. */
export const simularCobrancaSchema = z.object({
  evento: eventoDoAsaasSchema,
  /** Qual fatura receber o evento. Vazio = a última em aberto. */
  faturaId: z.string().uuid().nullish(),
});
export type SimularCobrancaInput = z.infer<typeof simularCobrancaSchema>;
